/**
 * useBlockDefinitionDialog — Track 3 Phase 2
 *
 * Owns the BLOCK command's post-selection dialog feature extracted from
 * `App.tsx` as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Mirrors `draftSession.step === "block-dialog"` into a local open
 *     flag (auto-open driven by the draft state machine — closes on
 *     other BLOCK steps and on null draft session).
 *   - Owns the Confirm / Cancel handlers. Confirm collects child
 *     entities from the geometry document, dispatches
 *     `geometry/create-block-definition` and clears the draft session;
 *     Cancel clears the draft session without dispatching.
 *   - Exposes `close()` so legacy resetShellState() still has a kill
 *     switch (matches the Phase 1 BlockEditExitDialog pattern).
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: {
 *       onConfirm,
 *       onCancel,
 *       selectedEntities,
 *       existingBlocks,
 *       initialBasePoint,
 *     },
 *     close,
 *   }
 *
 * The orchestrator spreads `props` onto `<BlockDefinitionDialog>` and
 * calls `close()` from `resetShellState()` for the imperative reset path.
 *
 * AutoCAD parity:
 *   AutoCAD's BLOCK / BMAKE command opens the "Block Definition" tabbed
 *   dialog (Objects / Behavior / Settings / Description). The hook only
 *   owns orchestration — the dialog component itself preserves the four
 *   tabs and INSUNITS dropdown verbatim.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import type { BlockDefinitionParams } from "../components/BlockDefinitionDialog";
import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";

import type { DraftSession } from "../scene/draftingHelpers";

export interface UseBlockDefinitionDialogParams {
  /** Current draft session — auto-opens dialog when step === "block-dialog". */
  draftSession: DraftSession | null;
  /** Selected entity previews fed to the dialog (Objects tab). */
  selectedEntities: EntityPreview[];
  /** Cleared by Confirm / Cancel handlers. */
  setDraftSession: (session: DraftSession | null) => void;
  /** Cleared by Confirm / Cancel handlers. */
  setCommandText: (text: string) => void;
  /** Receives localized success / error feed entries. */
  appendCommandFeed: (text: string) => void;
}

export interface UseBlockDefinitionDialogResult {
  /** Whether to mount `<BlockDefinitionDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<BlockDefinitionDialog>`. */
  props: {
    onConfirm: (params: BlockDefinitionParams) => void;
    onCancel: () => void;
    selectedEntities: EntityPreview[];
    existingBlocks: Record<string, unknown>;
    initialBasePoint?: { x: number; y: number };
  };
  /** Imperative close — used by `resetShellState()`. */
  close: () => void;
}

export function useBlockDefinitionDialog({
  draftSession,
  selectedEntities,
  setDraftSession,
  setCommandText,
  appendCommandFeed,
}: UseBlockDefinitionDialogParams): UseBlockDefinitionDialogResult {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);
  const activeSpaceId = useEditorStore(
    (store) => store.state.drawing.activeSpaceId,
  );

  const [isOpen, setIsOpen] = useState(false);

  // Auto-open driven by the draft state machine. Mirrors the original
  // multi-clause logic from App.tsx:3189-3217: open on `block-dialog`,
  // close on any other BLOCK step, close on null draft session.
  useEffect(() => {
    if (draftSession?.step === "block-dialog") {
      setIsOpen(true);
    } else if (draftSession?.command === "BLOCK") {
      setIsOpen(false);
    }
    if (!draftSession) {
      setIsOpen(false);
    }
  }, [draftSession]);

  // Pre-pop base point if user already picked one before the dialog.
  const initialBasePoint = useMemo<{ x: number; y: number } | undefined>(() => {
    const bx = Number(draftSession?.options?.blockBaseX);
    const by = Number(draftSession?.options?.blockBaseY);
    if (Number.isFinite(bx) && Number.isFinite(by)) {
      return { x: bx, y: by };
    }
    return undefined;
  }, [draftSession]);

  const closeAndClearDraft = useCallback(() => {
    setIsOpen(false);
    setDraftSession(null);
    setCommandText("");
  }, [setDraftSession, setCommandText]);

  const onConfirm = useCallback(
    (params: BlockDefinitionParams) => {
      // Collect the actual DrawingEntity shapes for the selected IDs from
      // the geometry document (shell's selectedEntities are EntityPreview
      // — we need the persisted entities for the block child list). We
      // clone via JSON round-trip so that the original entities (retain
      // mode) are not aliased in the block definition.
      const selectedIdSet = new Set(params.selectedEntityIds);
      const clonedEntities: unknown[] = [];
      for (const space of geometryDocument.spaces) {
        for (const entity of space.entities) {
          if (!selectedIdSet.has(entity.id)) continue;
          clonedEntities.push(JSON.parse(JSON.stringify(entity)));
        }
      }

      // Convert mode needs a stable new INSERT id; the reducer passes
      // it through unchanged so downstream selection / audit lines up.
      const convertInsertId =
        params.postAction === "convert"
          ? `block-insert-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`
          : undefined;

      try {
        dispatch({
          type: "geometry/create-block-definition",
          source: "toolbar",
          blockDef: {
            name: params.name,
            handle: "",
            layer: "0",
            flags: 0,
            basePoint: {
              x: params.basePoint.x,
              y: params.basePoint.y,
              z: 0,
            },
            xrefPathName: "",
            description: params.description,
            entities: clonedEntities,
          },
          blockRecord: {
            name: params.name,
            insertionUnits: params.blockUnit,
            explodable: params.allowExploding,
            scalable: params.scaleUniformly ? "uniform" : "non-uniform",
            isAnnotative: params.annotative,
            description: params.description,
          },
          postAction: params.postAction,
          sourceEntityIds: params.selectedEntityIds,
          convertSpaceId:
            params.postAction === "convert" ? activeSpaceId : undefined,
          convertInsertPoint:
            params.postAction === "convert" ? params.basePoint : undefined,
          convertInsertId,
        });
        appendCommandFeed(t("summary.block_defined", { name: params.name }));
        closeAndClearDraft();
      } catch (error) {
        // validateBlockName or schema parse failed. Keep the dialog
        // open so the user can correct the name / selection; surface
        // the error in the command feed so it does not silently
        // disappear.
        const message =
          error instanceof Error ? error.message : String(error);
        appendCommandFeed(t("toast.block.create_failed", { message }));
      }
    },
    [
      dispatch,
      geometryDocument,
      activeSpaceId,
      appendCommandFeed,
      t,
      closeAndClearDraft,
    ],
  );

  const onCancel = useCallback(() => {
    closeAndClearDraft();
  }, [closeAndClearDraft]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    props: {
      onConfirm,
      onCancel,
      selectedEntities,
      existingBlocks: geometryDocument.blocks ?? {},
      // Spread `initialBasePoint` only when defined — keeps the prop
      // omitted under `exactOptionalPropertyTypes`.
      ...(initialBasePoint !== undefined ? { initialBasePoint } : {}),
    },
    close,
  };
}
